import { useEffect, useState } from 'react'
import { styled } from 'styled-components'
import { UserProfile } from '../../../libs/profile-service'
import { getClasses, Class } from '../../../libs/class-service'
import ClassesList from './ClassesList'
import ClassesEmpty from './ClassesEmpty'
import ClassDetail from './ClassDetail'

interface Props {
    profileData?: UserProfile | null
}

const LoadingMessage = styled.div`
    padding: 20px;
    text-align: center;
    color: #666666;
    font-size: 16px;
`

const ErrorMessage = styled.div`
    padding: 20px;
    text-align: center;
    color: #ff4444;
    font-size: 16px;
    background-color: #ffe5e5;
    border-radius: 8px;
`

const Wrapper = styled.div`
    width: 100%;
`

export default function MyClass({ profileData }: Props) {
    const [classes, setClasses] = useState<Class[]>([])
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)
    const [selectedClass, setSelectedClass] = useState<Class | null>(null)
    const [wasClassSelected, setWasClassSelected] = useState(false)

    useEffect(() => {
        fetchClasses()
    }, [])

    useEffect(() => {
        // If returning from class detail (selectedClass becomes null after being set),
        // refetch the classes to get any updates made in ClassDetail
        if (wasClassSelected && selectedClass === null) {
            fetchClasses()
        }
        setWasClassSelected(selectedClass !== null)
    }, [selectedClass, wasClassSelected])

    const fetchClasses = async () => {
        try {
            setLoading(true)
            setError(null)
            const fetchedClasses = await getClasses()
            console.log('Fetched classes:', fetchedClasses)
            setClasses(fetchedClasses.classes || [])
        } catch (err) {
            const errorMsg =
                err instanceof Error ? err.message : 'Failed to load classes'
            console.error('Error loading classes:', err)
            console.error('Error message:', errorMsg)
            setError(errorMsg)
            setClasses([])
        } finally {
            setLoading(false)
        }
    }

    if (loading) {
        return <LoadingMessage>Loading classes...</LoadingMessage>
    }

    if (error) {
        return <ErrorMessage>{error}</ErrorMessage>
    }

    // Show class detail if one is selected
    if (selectedClass) {
        return (
            <Wrapper>
                <ClassDetail
                    classItem={selectedClass}
                    onBack={() => setSelectedClass(null)}
                />
            </Wrapper>
        )
    }

    return (
        <Wrapper>
            <ClassesList
                classes={classes}
                onManageClass={(classItem) => setSelectedClass(classItem)}
                onClassCreated={fetchClasses}
            />
        </Wrapper>
    )
}
